import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardHeader from '../components/DashboardHeader';
import MarkCompleteModal from '../components/MarkCompleteModal';
import ViewPhotosModal from '../components/ViewPhotosModal';
import MaterialDetailModal from '../components/MaterialDetailModal';
import CreateChangeRequestModal from '../components/CreateChangeRequestModal';
import EngineerChangeResponseModal from '../components/EngineerChangeResponseModal';
import AddMaterialModal from '../components/AddMaterialModal';
import WeeklyCalendar from '../components/WeeklyCalendar';
import ConstructionGanttChart from '../components/ConstructionGanttChart';
import RaiseInvoiceModal from '../components/RaiseInvoiceModal';
import GenerateReceiptModal from '../components/GenerateReceiptModal';
import CustomerPaymentModal from '../components/CustomerPaymentModal';
import MilestoneApprovalModal from '../components/MilestoneApprovalModal';
import { api, apiMultipart } from '../utils/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ArrowLeft, FileText, Calendar, Image, Package, FileQuestion, IndianRupee, Upload, CheckCircle2, Clock, AlertCircle, Download, Plus, Eye } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const ProjectDetailsEnhanced = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [project, setProject] = useState(null);
  const [milestones, setMilestones] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [changeRequests, setChangeRequests] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modal states
  const [showMarkComplete, setShowMarkComplete] = useState(false);
  const [selectedMilestone, setSelectedMilestone] = useState(null);
  const [showViewPhotos, setShowViewPhotos] = useState(false);
  const [showMaterialDetail, setShowMaterialDetail] = useState(null);
  const [showAddMaterial, setShowAddMaterial] = useState(false);
  const [showCreateChange, setShowCreateChange] = useState(false);
  const [showEngineerResponse, setShowEngineerResponse] = useState(false);
  const [selectedChangeRequest, setSelectedChangeRequest] = useState(null);
  
  // Payment modal states
  const [showRaiseInvoice, setShowRaiseInvoice] = useState(false);
  const [showGenerateReceipt, setShowGenerateReceipt] = useState(false);
  const [showCustomerPayment, setShowCustomerPayment] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  
  // Milestone approval modal state (admin)
  const [showMilestoneApproval, setShowMilestoneApproval] = useState(false);
  const [selectedMilestoneForApproval, setSelectedMilestoneForApproval] = useState(null);

  useEffect(() => {
    fetchData();
  }, [projectId]);

  const fetchData = async () => {
    try {
      const [userRes, projectRes, milestonesRes, documentsRes, updatesRes, materialsRes, crRes, paymentsRes] = await Promise.all([
        api.get('/auth/me'),
        api.get(`/projects/${projectId}`),
        api.get(`/milestones?project_id=${projectId}`),
        api.get(`/documents?project_id=${projectId}`),
        api.get(`/progress-updates?project_id=${projectId}`),
        api.get(`/materials?project_id=${projectId}`),
        api.get(`/change-requests?project_id=${projectId}`),
        api.get(`/payments?project_id=${projectId}`)
      ]);

      setUser(userRes.data);
      setProject(projectRes.data);
      setMilestones(milestonesRes.data);
      setDocuments(documentsRes.data);
      setUpdates(updatesRes.data);
      
      // Filter materials based on role
      if (userRes.data.role === 'customer') {
        setMaterials(materialsRes.data.filter(m => m.admin_approved));
      } else {
        setMaterials(materialsRes.data);
      }
      
      // Filter change requests based on role
      if (userRes.data.role === 'customer') {
        setChangeRequests(crRes.data.filter(cr => cr.admin_approved || cr.requested_by === userRes.data.user_id));
      } else {
        setChangeRequests(crRes.data);
      }
      
      setPayments(paymentsRes.data);
    } catch (error) {
      console.error('Failed to fetch project:', error);
      toast.error('Failed to load project details');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadDocument = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    formData.append('project_id', projectId);

    try {
      await apiMultipart.post('/documents/upload', formData);
      toast.success('Document uploaded successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to upload document');
    }
  };

  const handleApproveDocument = async (documentId) => {
    try {
      await api.post(`/documents/${documentId}/approve`);
      toast.success('Document approved');
      fetchData();
    } catch (error) {
      toast.error('Failed to approve document');
    }
  };

  const handleAdminApproveChangeResponse = async (requestId) => {
    try {
      await api.post(`/change-requests/${requestId}/admin-approve-response`);
      toast.success('Engineer response approved');
      fetchData();
    } catch (error) {
      toast.error('Failed to approve response');
    }
  };

  if (loading || !user || !project) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-100">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-orange-700 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-stone-600">Loading project...</p>
        </div>
      </div>
    );
  }

  const completedMilestones = milestones.filter(m => m.status === 'completed').length;
  const progressPercentage = milestones.length > 0 ? (completedMilestones / milestones.length) * 100 : 0;

  return (
    <div className="min-h-screen bg-stone-100" data-testid="project-details">
      <DashboardHeader user={user} unreadCount={0} />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <Button
            variant="ghost"
            onClick={() => navigate(`/${user.role}`)}
            className="mb-4"
            data-testid="back-button"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Dashboard
          </Button>

          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-heading font-bold text-stone-950 mb-2">
                {project.project_name}
              </h1>
              <p className="text-stone-600">{project.location}</p>
            </div>
            <Badge className="bg-green-100 text-green-700 hover:bg-green-100 rounded-sm">
              {project.status}
            </Badge>
          </div>

          {/* Project Overview */}
          <div className="grid md:grid-cols-4 gap-4 mt-6">
            {user.role !== 'engineer' && (
              <Card className="border-stone-200 rounded-sm shadow-sm">
                <CardContent className="p-4">
                  <p className="text-sm text-stone-600 mb-1">Contract Value</p>
                  <p className="text-xl font-heading font-bold text-stone-950">₹{project.contract_value.toLocaleString('en-IN')}</p>
                </CardContent>
              </Card>
            )}
            <Card className="border-stone-200 rounded-sm shadow-sm">
              <CardContent className="p-4">
                <p className="text-sm text-stone-600 mb-1">Built-up Area</p>
                <p className="text-xl font-heading font-bold text-stone-950">{project.built_up_area} sq.ft</p>
              </CardContent>
            </Card>
            <Card className="border-stone-200 rounded-sm shadow-sm">
              <CardContent className="p-4">
                <p className="text-sm text-stone-600 mb-1">Start Date</p>
                <p className="text-xl font-heading font-bold text-stone-950">
                  {new Date(project.start_date).toLocaleDateString('en-IN')}
                </p>
              </CardContent>
            </Card>
            <Card className="border-stone-200 rounded-sm shadow-sm">
              <CardContent className="p-4">
                <p className="text-sm text-stone-600 mb-1">Expected Completion</p>
                <p className="text-xl font-heading font-bold text-stone-950">
                  {new Date(project.expected_completion_date).toLocaleDateString('en-IN')}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Progress Bar */}
          <Card className="border-stone-200 rounded-sm shadow-sm mt-4">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-heading font-semibold text-stone-950">Overall Progress</h3>
                <span className="text-2xl font-heading font-bold text-orange-700">{progressPercentage.toFixed(0)}%</span>
              </div>
              <Progress value={progressPercentage} className="h-3" />
              <p className="text-sm text-stone-600 mt-2">
                {completedMilestones} of {milestones.length} milestones completed
              </p>
            </CardContent>
          </Card>
        </motion.div>

        {/* Tabs */}
        <Tabs defaultValue={user.role === 'customer' ? 'overview' : 'timeline'} className="space-y-6">
          <TabsList className="bg-white border border-stone-200 rounded-sm p-1">
            <TabsTrigger value="overview" className="rounded-sm" data-testid="overview-tab">
              <Calendar className="mr-2 h-4 w-4" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="timeline" className="rounded-sm" data-testid="timeline-tab">
              <Calendar className="mr-2 h-4 w-4" />
              Milestones
            </TabsTrigger>
            <TabsTrigger value="documents" className="rounded-sm" data-testid="documents-tab">
              <FileText className="mr-2 h-4 w-4" />
              Documents
            </TabsTrigger>
            <TabsTrigger value="updates" className="rounded-sm" data-testid="updates-tab">
              <Image className="mr-2 h-4 w-4" />
              Updates
            </TabsTrigger>
            <TabsTrigger value="materials" className="rounded-sm" data-testid="materials-tab">
              <Package className="mr-2 h-4 w-4" />
              Materials
            </TabsTrigger>
            <TabsTrigger value="changes" className="rounded-sm" data-testid="changes-tab">
              <FileQuestion className="mr-2 h-4 w-4" />
              Changes
            </TabsTrigger>
            {user.role !== 'engineer' && (
              <TabsTrigger value="payments" className="rounded-sm" data-testid="payments-tab">
                <IndianRupee className="mr-2 h-4 w-4" />
                Payments
              </TabsTrigger>
            )}
          </TabsList>

          {/* Overview Tab - Gantt Chart for all roles */}
          <TabsContent value="overview">
            <ConstructionGanttChart
              milestones={milestones}
              projectStartDate={project.start_date}
              projectEndDate={project.expected_completion_date}
            />
          </TabsContent>

          {/* Timeline Tab */}
          <TabsContent value="timeline">
            <Card className="border-stone-200 rounded-sm shadow-sm">
              <CardHeader>
                <CardTitle className="text-2xl font-heading">Construction Timeline</CardTitle>
                <CardDescription>Track milestones and completion dates</CardDescription>
              </CardHeader>
              <CardContent>
                {milestones.length === 0 ? (
                  <div className="text-center py-12">
                    <Calendar className="h-16 w-16 text-stone-300 mx-auto mb-4" />
                    <p className="text-stone-600">No milestones defined yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {milestones.map((milestone) => (
                      <div key={milestone.milestone_id} className="border border-stone-200 rounded-lg p-5 hover:shadow-sm transition-shadow" data-testid={`milestone-${milestone.milestone_id}`}>
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3">
                              <h3 className="text-lg font-semibold text-stone-900">{milestone.stage_name}</h3>
                              <Badge className={`rounded-full px-3 ${
                                milestone.status === 'completed' ? 'bg-green-100 text-green-700' :
                                milestone.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                                milestone.status === 'review' ? 'bg-blue-100 text-blue-700' :
                                milestone.status === 'rejected' ? 'bg-red-100 text-red-700' :
                                milestone.status === 'on_hold' ? 'bg-orange-100 text-orange-700' :
                                milestone.status === 'in_progress' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-stone-200 text-stone-700'
                              }`}>
                                {milestone.status === 'review' ? 'Pending Admin Review' : 
                                 milestone.status === 'approved' ? 'Pending Customer Approval' :
                                 milestone.status === 'on_hold' ? 'On Hold' :
                                 milestone.status.replace('_', ' ')}
                              </Badge>
                            </div>
                            <p className="text-sm text-stone-600 mt-1">{milestone.description}</p>
                            {milestone.admin_feedback && (
                              <p className="text-sm text-blue-600 mt-2 bg-blue-50 p-2 rounded">
                                <strong>Admin Feedback:</strong> {milestone.admin_feedback}
                              </p>
                            )}
                          </div>
                          <div className="flex space-x-2 ml-4">
                            {/* Engineer: Mark Complete */}
                            {user.role === 'engineer' && (milestone.status === 'in_progress' || milestone.status === 'rejected') && !milestone.engineer_approved && (
                              <Button
                                size="sm"
                                onClick={() => {
                                  setSelectedMilestone(milestone);
                                  setShowMarkComplete(true);
                                }}
                                className="bg-blue-900 hover:bg-blue-800 text-white rounded-lg"
                              >
                                <CheckCircle2 className="mr-1 h-4 w-4" />
                                Mark Complete
                              </Button>
                            )}
                            {/* Admin: Review and Approve/Reject/Hold */}
                            {user.role === 'admin' && milestone.status === 'review' && (
                              <Button
                                size="sm"
                                onClick={() => {
                                  setSelectedMilestoneForApproval(milestone);
                                  setShowMilestoneApproval(true);
                                }}
                                className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
                              >
                                <Eye className="mr-1 h-4 w-4" />
                                Review & Approve
                              </Button>
                            )}
                            {/* Customer: Approve after admin approval */}
                            {user.role === 'customer' && milestone.status === 'approved' && milestone.admin_approved && !milestone.customer_approved && (
                              <Button
                                size="sm"
                                onClick={async () => {
                                  try {
                                    await api.post(`/milestones/${milestone.milestone_id}/approve`, { action: 'approve' });
                                    toast.success('Milestone approved and marked complete');
                                    fetchData();
                                  } catch (error) {
                                    toast.error('Failed to approve milestone');
                                  }
                                }}
                                className="bg-green-600 hover:bg-green-700 text-white rounded-lg"
                              >
                                <CheckCircle2 className="mr-1 h-4 w-4" />
                                Approve
                              </Button>
                            )}
                            {/* View Photos */}
                            {milestone.completion_photos && milestone.completion_photos.length > 0 && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setSelectedMilestone(milestone);
                                  setShowViewPhotos(true);
                                }}
                                className="rounded-lg"
                                data-testid={`view-photos-${milestone.milestone_id}`}
                              >
                                <Eye className="mr-1 h-4 w-4" />
                                View Photos
                              </Button>
                            )}
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4 mt-3 text-sm">
                          <div>
                            <p className="text-stone-600">Expected Start</p>
                            <p className="font-medium text-stone-900">{new Date(milestone.expected_start).toLocaleDateString('en-IN')}</p>
                          </div>
                          <div>
                            <p className="text-stone-600">Expected End</p>
                            <p className="font-medium text-stone-900">{new Date(milestone.expected_end).toLocaleDateString('en-IN')}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents">
            <Card className="border-stone-200 rounded-sm shadow-sm">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-2xl font-heading">Documents</CardTitle>
                    <CardDescription>Drawings, contracts, and approvals</CardDescription>
                  </div>
                  {(user.role === 'admin' || user.role === 'engineer') && (
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button className="bg-orange-700 hover:bg-orange-800 text-white rounded-sm" data-testid="upload-document-button">
                          <Upload className="mr-2 h-4 w-4" />
                          Upload
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Upload Document</DialogTitle>
                          <DialogDescription>Add a new document to the project</DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleUploadDocument} className="space-y-4">
                          <div className="space-y-2">
                            <Label htmlFor="doc_name">Document Name</Label>
                            <Input id="doc_name" name="name" placeholder="Floor Plan v1" required />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="category">Category</Label>
                            <Input id="category" name="category" placeholder="Architectural" required />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="file">File</Label>
                            <Input id="file" name="file" type="file" required />
                          </div>
                          <Button type="submit" className="w-full bg-orange-700 hover:bg-orange-800 text-white rounded-sm">
                            Upload Document
                          </Button>
                        </form>
                      </DialogContent>
                    </Dialog>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {documents.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="h-16 w-16 text-stone-300 mx-auto mb-4" />
                    <p className="text-stone-600">No documents uploaded yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {documents.map((doc) => (
                      <div key={doc.document_id} className="border border-stone-200 rounded-sm p-4 hover:shadow-sm transition-shadow" data-testid={`document-${doc.document_id}`}>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-1">
                              <FileText className="h-5 w-5 text-orange-700" />
                              <h3 className="font-heading font-semibold text-stone-950">{doc.name}</h3>
                              <Badge variant="outline" className="text-xs">{doc.category}</Badge>
                              <Badge variant="outline" className="text-xs">v{doc.version}</Badge>
                            </div>
                            <p className="text-sm text-stone-600">
                              Uploaded {new Date(doc.uploaded_at).toLocaleDateString('en-IN')}
                            </p>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Badge className={`rounded-sm ${
                              doc.status === 'approved' ? 'bg-green-100 text-green-700' :
                              'bg-yellow-100 text-yellow-700'
                            }`}>
                              {doc.status}
                            </Badge>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-xs"
                              data-testid={`download-document-${doc.document_id}`}
                            >
                              <Download className="mr-1 h-3 w-3" />
                              Download
                            </Button>
                            {user.role === 'customer' && doc.status === 'pending' && (
                              <Button
                                size="sm"
                                onClick={() => handleApproveDocument(doc.document_id)}
                                className="bg-green-700 hover:bg-green-800 text-white rounded-sm"
                                data-testid={`approve-document-${doc.document_id}`}
                              >
                                Approve
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Updates Tab */}
          <TabsContent value="updates">
            <div className="space-y-6">
              <WeeklyCalendar 
                projectId={projectId} 
                userRole={user.role}
                projectStartDate={project.start_date}
              />
              
              <Card className="border-stone-200 rounded-sm shadow-sm">
                <CardHeader>
                  <CardTitle className="text-2xl font-heading">Recent Updates</CardTitle>
                  <CardDescription>Detailed progress updates and photos</CardDescription>
                </CardHeader>
                <CardContent>
                  {updates.length === 0 ? (
                    <div className="text-center py-12">
                      <Image className="h-16 w-16 text-stone-300 mx-auto mb-4" />
                      <p className="text-stone-600">No updates posted yet</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {updates.map((update) => (
                        <div key={update.update_id} className="border border-stone-200 rounded-sm p-6" data-testid={`update-${update.update_id}`}>
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <h3 className="text-lg font-heading font-semibold text-stone-950 mb-1">{update.title}</h3>
                              <p className="text-sm text-stone-600">
                                Posted by {update.uploaded_by_name} • {new Date(update.created_at).toLocaleDateString('en-IN')}
                              </p>
                            </div>
                          </div>
                          <p className="text-stone-700 mb-4">{update.description}</p>
                          {update.media_files && update.media_files.length > 0 && (
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                              {update.media_files.map((file, idx) => {
                                const src = file && file.startsWith('/')
                                  ? `${process.env.REACT_APP_BACKEND_URL}${file}`
                                  : file;
                                return (
                                  <a
                                    key={idx}
                                    href={src}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="aspect-video bg-stone-100 rounded-md overflow-hidden hover:opacity-90 transition-opacity"
                                    data-testid={`progress-media-${update.update_id}-${idx}`}
                                  >
                                    <img
                                      src={src}
                                      alt={`Update media ${idx + 1}`}
                                      className="w-full h-full object-cover"
                                      onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.parentElement.innerHTML = '<div class="flex items-center justify-center h-full text-stone-400 text-xs">Image unavailable</div>';
                                      }}
                                    />
                                  </a>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Materials Tab */}
          <TabsContent value="materials">
            <Card className="border-stone-200 rounded-sm shadow-sm">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-2xl font-heading">Material Approvals</CardTitle>
                    <CardDescription>Review and approve construction materials</CardDescription>
                  </div>
                  {user.role === 'engineer' && (
                    <Button
                      onClick={() => setShowAddMaterial(true)}
                      className="bg-orange-700 hover:bg-orange-800 text-white rounded-sm"
                      data-testid="add-material-button"
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      Add Material
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {materials.length === 0 ? (
                  <div className="text-center py-12">
                    <Package className="h-16 w-16 text-stone-300 mx-auto mb-4" />
                    <p className="text-stone-600">No materials for approval</p>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 gap-4">
                    {materials.map((material) => (
                      <div key={material.material_id} className="border border-stone-200 rounded-sm p-4 hover:shadow-md transition-shadow cursor-pointer" 
                           onClick={() => setShowMaterialDetail(material)}
                           data-testid={`material-${material.material_id}`}>
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-1">
                              <h3 className="text-lg font-heading font-semibold text-stone-950">{material.name}</h3>
                              <Badge className={`rounded-sm ${
                                material.status === 'approved' ? 'bg-green-100 text-green-700' :
                                material.status === 'rejected' ? 'bg-red-100 text-red-700' :
                                material.admin_approved ? 'bg-blue-100 text-blue-700' :
                                'bg-yellow-100 text-yellow-700'
                              }`}>
                                {material.admin_approved ? (material.status === 'pending' ? 'Pending' : material.status) : 'Under Review'}
                              </Badge>
                            </div>
                            <p className="text-sm text-stone-600 mb-2">{material.category}</p>
                            {user.role !== 'engineer' && (
                              <p className="text-lg font-heading font-bold text-orange-700">₹{material.estimated_cost.toLocaleString('en-IN')}</p>
                            )}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className="w-full mt-2"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowMaterialDetail(material);
                          }}
                        >
                          <Eye className="mr-2 h-4 w-4" />
                          View Details
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Changes Tab */}
          <TabsContent value="changes">
            <Card className="border-stone-200 rounded-sm shadow-sm">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-2xl font-heading">Change Requests</CardTitle>
                    <CardDescription>Track variations and modifications</CardDescription>
                  </div>
                  {user.role === 'customer' && (
                    <Button
                      onClick={() => setShowCreateChange(true)}
                      className="bg-orange-700 hover:bg-orange-800 text-white rounded-sm"
                      data-testid="create-change-request-button"
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      Request Change
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {changeRequests.length === 0 ? (
                  <div className="text-center py-12">
                    <FileQuestion className="h-16 w-16 text-stone-300 mx-auto mb-4" />
                    <p className="text-stone-600">No change requests</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {changeRequests.map((cr) => (
                      <div key={cr.request_id} className="border border-stone-200 rounded-sm p-4" data-testid={`change-request-${cr.request_id}`}>
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-1">
                              <h3 className="font-heading font-semibold text-stone-950">{cr.stage || 'Change Request'}</h3>
                              <Badge className={`rounded-sm ${
                                cr.status === 'approved' ? 'bg-green-100 text-green-700' :
                                cr.status === 'rejected' ? 'bg-red-100 text-red-700' :
                                cr.engineer_decision && !cr.admin_approved ? 'bg-blue-100 text-blue-700' :
                                'bg-yellow-100 text-yellow-700'
                              }`}>
                                {cr.engineer_decision && !cr.admin_approved ? 'Awaiting Admin' : cr.status}
                              </Badge>
                            </div>
                            <p className="text-sm text-stone-600 mb-2">
                              Requested by {cr.requested_by_name} • {new Date(cr.created_at).toLocaleDateString('en-IN')}
                            </p>
                            <p className="text-stone-700 mb-3">{cr.description}</p>
                            {cr.engineer_remarks && cr.admin_approved && (
                              <div className="bg-blue-50 border border-blue-200 rounded-sm p-3 mb-3">
                                <p className="text-sm font-semibold text-blue-900 mb-1">Engineer Response:</p>
                                <p className="text-sm text-blue-900">{cr.engineer_remarks}</p>
                                {cr.cost_impact && (
                                  <p className="text-sm text-blue-900 mt-2"><strong>Cost Impact:</strong> ₹{cr.cost_impact.toLocaleString('en-IN')}</p>
                                )}
                                {cr.timeline_impact && (
                                  <p className="text-sm text-blue-900"><strong>Timeline Impact:</strong> {cr.timeline_impact}</p>
                                )}
                              </div>
                            )}
                          </div>
                          {user.role === 'engineer' && cr.status === 'pending' && !cr.engineer_decision && (
                            <Button
                              size="sm"
                              onClick={() => {
                                setSelectedChangeRequest(cr);
                                setShowEngineerResponse(true);
                              }}
                              className="bg-orange-700 hover:bg-orange-800 text-white ml-4"
                            >
                              Respond
                            </Button>
                          )}
                          {user.role === 'admin' && cr.engineer_decision && !cr.admin_approved && (
                            <Button
                              size="sm"
                              onClick={() => handleAdminApproveChangeResponse(cr.request_id)}
                              className="bg-green-700 hover:bg-green-800 text-white ml-4"
                            >
                              Approve Response
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Payments Tab */}
          {user.role !== 'engineer' && (
            <TabsContent value="payments">
              <Card className="border-stone-200 rounded-sm shadow-sm">
                <CardHeader>
                  <CardTitle className="text-2xl font-heading">Payment Milestones</CardTitle>
                  <CardDescription>Track milestone-based payments</CardDescription>
                </CardHeader>
                <CardContent>
                  {payments.length === 0 ? (
                    <div className="text-center py-12">
                      <IndianRupee className="h-16 w-16 text-stone-300 mx-auto mb-4" />
                      <p className="text-stone-600">No payment milestones defined</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {payments.map((payment) => (
                        <div key={payment.payment_id} className="border border-stone-200 rounded-lg p-5 hover:shadow-sm transition-shadow" data-testid={`payment-${payment.payment_id}`}>
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-3 mb-3">
                                <h3 className="text-lg font-semibold text-stone-900">{payment.stage_name}</h3>
                                <Badge className={`rounded-full px-3 ${
                                  payment.status === 'paid' ? 'bg-green-100 text-green-700' :
                                  payment.status === 'invoiced' ? 'bg-blue-100 text-blue-700' :
                                  payment.status === 'partial' ? 'bg-orange-100 text-orange-700' :
                                  payment.status === 'overdue' ? 'bg-red-100 text-red-700' :
                                  'bg-stone-100 text-stone-700'
                                }`}>
                                  {payment.status === 'invoiced' ? 'Invoice Sent' : payment.status}
                                </Badge>
                              </div>
                              <div className="grid grid-cols-3 gap-6 text-sm">
                                <div>
                                  <p className="text-stone-500 mb-1">Amount</p>
                                  <p className="text-xl font-bold text-stone-900">₹{payment.amount.toLocaleString('en-IN')}</p>
                                </div>
                                <div>
                                  <p className="text-stone-500 mb-1">Due Date</p>
                                  <p className="font-medium text-stone-900">
                                    {new Date(payment.due_date).toLocaleDateString('en-IN', {
                                      day: 'numeric',
                                      month: 'short',
                                      year: 'numeric'
                                    })}
                                  </p>
                                </div>
                                {payment.paid_date && (
                                  <div>
                                    <p className="text-stone-500 mb-1">Paid On</p>
                                    <p className="font-medium text-green-700">
                                      {new Date(payment.paid_date).toLocaleDateString('en-IN', {
                                        day: 'numeric',
                                        month: 'short',
                                        year: 'numeric'
                                      })}
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>
                            
                            {/* Admin Actions */}
                            {user.role === 'admin' && (
                              <div className="flex space-x-2 ml-4">
                                {payment.status === 'pending' && (
                                  <Button
                                    size="sm"
                                    onClick={() => {
                                      setSelectedPayment(payment);
                                      setShowRaiseInvoice(true);
                                    }}
                                    className="bg-stone-900 hover:bg-stone-800 text-white rounded-lg"
                                    data-testid={`raise-invoice-${payment.payment_id}`}
                                  >
                                    <Upload className="mr-2 h-4 w-4" />
                                    Raise Invoice
                                  </Button>
                                )}
                                {payment.status === 'paid' && (
                                  <Button
                                    size="sm"
                                    onClick={() => {
                                      setSelectedPayment(payment);
                                      setShowGenerateReceipt(true);
                                    }}
                                    className="bg-green-600 hover:bg-green-700 text-white rounded-lg"
                                    data-testid={`generate-receipt-${payment.payment_id}`}
                                  >
                                    <FileText className="mr-2 h-4 w-4" />
                                    Generate Receipt
                                  </Button>
                                )}
                              </div>
                            )}
                            
                            {/* Customer Actions */}
                            {user.role === 'customer' && (
                              <div className="flex space-x-2 ml-4">
                                {payment.status === 'paid' && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="rounded-lg"
                                    data-testid={`download-receipt-${payment.payment_id}`}
                                  >
                                    <Download className="mr-2 h-4 w-4" />
                                    Receipt
                                  </Button>
                                )}
                                {(payment.status === 'pending' || payment.status === 'invoiced') && (
                                  <>
                                    {payment.invoice_id && (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="rounded-lg"
                                        data-testid={`view-invoice-${payment.payment_id}`}
                                      >
                                        View Invoice
                                      </Button>
                                    )}
                                    <Button
                                      size="sm"
                                      onClick={() => {
                                        setSelectedPayment(payment);
                                        setShowCustomerPayment(true);
                                      }}
                                      className="bg-green-600 hover:bg-green-700 text-white rounded-lg"
                                      data-testid={`make-payment-${payment.payment_id}`}
                                    >
                                      Make Payment
                                    </Button>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </main>

      {/* Modals */}
      <MarkCompleteModal
        milestone={selectedMilestone}
        open={showMarkComplete}
        onClose={() => {
          setShowMarkComplete(false);
          setSelectedMilestone(null);
        }}
        onSuccess={fetchData}
      />

      <ViewPhotosModal
        milestone={selectedMilestone}
        open={showViewPhotos}
        onClose={() => {
          setShowViewPhotos(false);
          setSelectedMilestone(null);
        }}
        onSuccess={fetchData}
        userRole={user.role}
      />

      <MaterialDetailModal
        material={showMaterialDetail}
        open={!!showMaterialDetail}
        onClose={() => setShowMaterialDetail(null)}
        onSuccess={fetchData}
        userRole={user.role}
      />

      <AddMaterialModal
        projectId={projectId}
        open={showAddMaterial}
        onClose={() => setShowAddMaterial(false)}
        onSuccess={fetchData}
      />

      <CreateChangeRequestModal
        projectId={projectId}
        milestones={milestones}
        open={showCreateChange}
        onClose={() => setShowCreateChange(false)}
        onSuccess={fetchData}
      />

      <EngineerChangeResponseModal
        changeRequest={selectedChangeRequest}
        open={showEngineerResponse}
        onClose={() => {
          setShowEngineerResponse(false);
          setSelectedChangeRequest(null);
        }}
        onSuccess={fetchData}
      />

      {/* Payment Modals */}
      <RaiseInvoiceModal
        payment={selectedPayment}
        open={showRaiseInvoice}
        onClose={() => {
          setShowRaiseInvoice(false);
          setSelectedPayment(null);
        }}
        onSuccess={fetchData}
      />

      <GenerateReceiptModal
        payment={selectedPayment}
        open={showGenerateReceipt}
        onClose={() => {
          setShowGenerateReceipt(false);
          setSelectedPayment(null);
        }}
        onSuccess={fetchData}
      />

      <CustomerPaymentModal
        payment={selectedPayment}
        open={showCustomerPayment}
        onClose={() => {
          setShowCustomerPayment(false);
          setSelectedPayment(null);
        }}
        onSuccess={fetchData}
      />

      {/* Milestone Approval Modal (Admin) */}
      <MilestoneApprovalModal
        milestone={selectedMilestoneForApproval}
        open={showMilestoneApproval}
        onClose={() => {
          setShowMilestoneApproval(false);
          setSelectedMilestoneForApproval(null);
        }}
        onSuccess={fetchData}
      />
    </div>
  );
};

export default ProjectDetailsEnhanced;